import {
  type ImageUploadTooltipCopy,
  imageUploadTooltipCopySchema,
} from '@/admin-v2/tools/image-upload-tooltip-copy';
import { isAdminV2UserFacingToolField } from '@/admin-v2/tools/tool-field-visibility';
import {
  type AdminV2PublicWorkspaceToolRuntimePackage,
  createAdminV2PublicWorkspaceToolRuntimePackageSchemaForLocale,
} from '@/admin-v2/workspace/workspace-runtime';
import type { CmsWorkspaceShellModel } from '@/cms/components/blocks/workspace/workspace-1/contracts/server-model';
import {
  type UpscalerSizeCopy,
  upscalerSizeCopySchema,
} from '@/workspace/code-owned/upscaler-size-copy';
import { z } from 'zod';

const previewImageSchema = z
  .object({
    src: z.string().min(1),
    alt: z.string(),
    dimensions: z
      .object({
        width: z.number().int().positive(),
        height: z.number().int().positive(),
      })
      .strict(),
  })
  .strict();

function createWorkspaceToolResponseSchema(locale: string) {
  return z
    .object({
      runtimePackage:
        createAdminV2PublicWorkspaceToolRuntimePackageSchemaForLocale(locale),
      startup: z
        .object({
          toolId: z.string().min(1),
          input: z.record(z.string(), z.unknown()),
          autoSubmit: z.boolean(),
          missingRequiredFieldIds: z.array(z.string().min(1)),
        })
        .strict(),
      toolPresentation: z
        .object({
          upscaleSizeCopy: upscalerSizeCopySchema.optional(),
          imageUploadTooltipCopy: imageUploadTooltipCopySchema.optional(),
          hero: z
            .object({
              eyebrow: z.string(),
              title: z.string(),
              subtitle: z.string(),
              previewImages: z.array(previewImageSchema),
            })
            .strict(),
        })
        .strict(),
    })
    .strict();
}

export type SiteRuntimeWorkspaceToolResponse = {
  runtimePackage: AdminV2PublicWorkspaceToolRuntimePackage;
  startup: {
    toolId: string;
    input: Record<string, unknown>;
    autoSubmit: boolean;
    missingRequiredFieldIds: string[];
  };
  toolPresentation: {
    hero: CmsWorkspaceShellModel['hero'];
    upscaleSizeCopy?: UpscalerSizeCopy;
    imageUploadTooltipCopy?: ImageUploadTooltipCopy;
  };
};

export function parseSiteRuntimeWorkspaceToolResponse(
  payload: unknown,
  requestedToolId: string,
  locale: string
): SiteRuntimeWorkspaceToolResponse | null {
  const parsed = createWorkspaceToolResponseSchema(locale).safeParse(payload);
  if (
    !parsed.success ||
    parsed.data.runtimePackage.toolId !== requestedToolId ||
    parsed.data.startup.toolId !== requestedToolId
  ) {
    return null;
  }
  if (
    parsed.data.runtimePackage.toolFields.some(
      (field) =>
        isAdminV2UserFacingToolField(field) &&
        field.workspaceControlVariant === 'upscale-size'
    ) &&
    !parsed.data.toolPresentation.upscaleSizeCopy
  )
    return null;

  if (
    parsed.data.runtimePackage.toolFields.some(
      (field) =>
        field.control === 'image_file' &&
        field.prefillable &&
        isAdminV2UserFacingToolField(field)
    ) &&
    !parsed.data.toolPresentation.imageUploadTooltipCopy
  )
    return null;
  return parsed.data as SiteRuntimeWorkspaceToolResponse;
}
